<template lang="pug">
  reporting-layout
    v-container.main-container(fluid)
      v-layout.header-title(align-center, justify-start, row, fill-height)
        action-title-bar.title-bar(is-inline, title='Activity Report', hideSubtitle) 
        v-menu.dates-menu(offset-y)
          v-toolbar-title.pt-0.liondesk.menu-toolbar-title(slot='activator')
            span.menu-toolbar-title--text.text-capitalize {{ dateMenuTitle }}
            v-icon.menu-toolbar-title--icon(color='primary', right) arrow_drop_down
          v-list
            v-list-tile(
              v-for='range in ranges',
              :key='range.value',
              @click='selectedDateMenuItem(range)'
            ) 
              v-list-tile-title.menu-date-caption 
                | {{ range.text }}
                span.menu-date-caption--subtext {{ range.hint }}
        v-layout.checkbox-container(v-if='comparedPeriodSubtitle', align-start, justify-start, row)
          input.mt-2.input-checkbox(type='checkbox', v-model='includeComparedPercentage')
          v-layout.mt-1.compare-labels-container(py-6, column)
            p.ma-0(:class='includeComparedPercentage ? "compare-label-title" : "compare-label-title--inactive"') {{ comparisonTitle(includeComparedPercentage) }}
            p.ma-0 {{ comparedPeriodSubtitle }}
        v-spacer.grow
        filter-report(@click:filter="filterResults")
      reporting-graph(
        :headers="headers"
        :selectedDateRange="selectedDateRange" 
        :selectedDateInterval="selectedDateInterval"
        :includeComparedPercentage="includeComparedPercentage"
      )
      v-layout(fluid)
        v-flex(xs12)
          v-card.data-table-container
            v-layout.data-table-header(align-center, justify-start, row, fill-height)
              column-sorting(v-model='reportingV2Headers', type='report', pinnable-columns)
                template(slot='activator')
                  v-btn
                    v-icon view_comfy
                    | Customize
              v-spacer.grow
              export-report-modal(
                :dateRange='dateMenuTitle'
                :headers="visibleHeaders" 
                :exportData='membersData' 
                :includeComparedPercentage="includeComparedPercentage"
              )
            v-data-table.reporting-table(
            hide-actions,
            dense,
            :headers='visibleHeaders',
            :items='updatedData'
            :loading='isLoading'
            )
              template(slot="headers" slot-scope="visibleHeaders")
                list-header(
                  :headers="visibleHeaders"
                  @click:sort="sortDir"
                  :currentSortDir="currentSortDir"
                )
              template(v-if="!isLoading" slot="items" slot-scope="props")
                list-data(
                  :data="props"
                  :headers="visibleHeaders"
                  :includeComparedPercentage="includeComparedPercentage"
                )
              
</template>

<script>
import ReportingLayout from './Layout';
import { mapState, mapActions, mapGetters } from 'vuex';
import ActionTitleBar from '@/Core/components/ActionTitleBar';
import ColumnSorting from '@/Core/components/tables/ColumnSorting';
import ExportReportModal from './components/ExportReportModal';
import ReportingGraph from './components/ReportingGraph';
import LiondeskDialog from '@/Core/components/LiondeskDialog';
import ListData from './components/ListData';
import ListHeader from './components/ListHeader';
import FilterReport from '@/Reporting/components/FilterReport';
import {
  ALL_TIME,
  LAST_7_DAYS,
  LAST_7_DAYS_VAL,
  ALL_TIME_VAL,
  EMAIL_SENT,
  ASC,
  DESC,
} from '@/Reporting/config/constants';

export default {
  name: 'ReportingV2',
  components: {
    ReportingLayout,
    ActionTitleBar,
    ColumnSorting,
    ExportReportModal,
    ReportingGraph,
    LiondeskDialog,
    ListData,
    ListHeader,
    FilterReport,
  },
  data() {
    return {
      dateMenuTitle: '',
      selectedDateInterval: LAST_7_DAYS_VAL,
      comparedPeriodSubtitle: '',
      selected: [],
      includeComparedPercentage: false,
      selectedDateRange: LAST_7_DAYS,
      updatedData: this.membersData,
      currentSortDir: ASC,
      currentSort: EMAIL_SENT,
      tableHeaders: [],
      filterSelection: [],
    };
  },
  computed: {
    ...mapState('reportingV2', ['headers', 'membersData', 'ranges', 'isLoading']),
    visibleHeaders() {
      return this.headers.filter(column => column.visible);
    },
    reportingV2Headers: {
      get() {
        const headers = this.headers.map(column => Object.assign({}, column));
        return headers.filter(({ text }) => text !== 'Name');
      },
      set(value) {
        this.updateHeaders(value);
      },
    },
    watchSelections() {
      return [this.tableHeaders, this.selectedDateInterval, this.filterSelection];
    },
  },
  watch: {
    ranges(value) {
      if (!value.length) return;
      const dateMenuTitleStringWithBrackets = value.find(
        ({ value }) => value === LAST_7_DAYS_VAL,
      ).hint;
      this.dateMenuTitle = dateMenuTitleStringWithBrackets.slice(1, -1);

      const comparedPeriodStringWithBrackets = value.find(
        ({ value }) => value === LAST_7_DAYS_VAL,
      ).compared_period;
      this.comparedPeriodSubtitle = comparedPeriodStringWithBrackets.slice(1, -1);
    },
    headers(value) {
      this.tableHeaders = value;
    },
    watchSelections(newValue, oldValue) {
      if (newValue === oldValue || !newValue.length) return;

      const [tableHeaders, dateRange, filter] = newValue;

      this.tableHeaders = tableHeaders;
      this.graphDateRange = dateRange;
      this.filterSelection = filter;
      this.searchTableData();
    },
    membersData() {
      this.sortData();
    },
    updatedData() {
      if (document.readyState === 'complete') {
        setTimeout(() => {
          this.setLeftToItemsPinned();
        }, 500);
      } else {
        window.addEventListener('load', () => {
          this.setLeftToItemsPinned();
        });
      }
    },
  },
  created() {
    this.fetchReportingV2Headers();
    this.tableHeaders = [...this.headers];
    this.fetchReportingV2DateRanges();
    this.fetchReportingV2DateRangesHints();

    this.searchTableData();
    this.sortData();
  },
  methods: {
    ...mapActions('reportingV2', [
      'fetchReportingV2Headers',
      'fetchReportingV2DateRanges',
      'fetchReportingV2DateRangesHints',
      'updateHeaders',
      'getInfoForDataTable',
    ]),
    async searchTableData() {
      await this.getInfoForDataTable({
        selectedHeaders: this.tableHeaders,
        dateRange: this.selectedDateInterval,
        selectedFilter: this.filterSelection,
      });
    },
    setLeftToItemsPinned() {
      const reportingTable = document.querySelector('.reporting-table');
      if (!reportingTable) return;

      const itemsPinned = reportingTable.querySelectorAll('th.pinned');
      const nameColumnWidth = reportingTable.querySelector('thead th:first-child').offsetWidth;
      const [firstPinnedColumn, secondPinnedColumn, thirdPinnedColumn] = itemsPinned;

      const secondPinnedColumnWidth = firstPinnedColumn
        ? firstPinnedColumn.offsetWidth + nameColumnWidth
        : 0;
      reportingTable.querySelectorAll('th:nth-of-type(3), td:nth-of-type(3)').forEach(element => {
        element.style.left = secondPinnedColumnWidth ? secondPinnedColumnWidth + 'px' : 'auto';
      });

      const thirdPinnedColumnWidth =
        secondPinnedColumn && thirdPinnedColumn
          ? secondPinnedColumn.offsetWidth + secondPinnedColumnWidth
          : 0;
      reportingTable.querySelectorAll('th:nth-of-type(4), td:nth-of-type(4)').forEach(element => {
        element.style.left = thirdPinnedColumnWidth ? thirdPinnedColumnWidth + 'px' : 'auto';
      });
    },
    selectedDateMenuItem(value) {
      if (value.value === ALL_TIME_VAL) {
        this.dateMenuTitle = ALL_TIME;
        this.selectedDateRange = this.dateMenuTitle;
        this.selectedDateInterval = value.value;
        this.includeComparedPercentage = false;
      } else {
        this.selectedDateRange = value.text;
        const dateMenuTitleStringWithBrackets = value.hint;
        this.dateMenuTitle = dateMenuTitleStringWithBrackets.slice(1, -1);
        this.selectedDateInterval = value.value;
      }
      this.searchTableData();
      const comparedPeriodStringWithBrackets = value.compared_period;
      this.comparedPeriodSubtitle = comparedPeriodStringWithBrackets.slice(1, -1);
    },
    comparisonTitle(value) {
      if (value) return 'Compared to previous period';

      return 'Compare to previous period';
    },
    sortDir(eventType) {
      this.currentSortDir = this.currentSortDir === ASC ? DESC : ASC;
      this.currentSort = eventType;
      this.sortData();
    },
    sortData() {
      const data = [...this.membersData];

      data.sort((a, b) => {
        let modifier = 1;
        if (this.currentSortDir === DESC) modifier = -1;
        if (this.countFromUser(a) < this.countFromUser(b)) {
          return -1 * modifier;
        }
        if (this.countFromUser(a) > this.countFromUser(b)) {
          return 1 * modifier;
        }
        return 0;
      });
      this.updatedData = data;
    },
    countFromUser(value) {
      const event = this.currentSort;
      const findEvent = value.events.find(e => e[event]);
      return findEvent[event].count;
    },
    filterResults(value) {
      this.filterSelection = value;
    },
  },
};
</script>

<style lang="scss" scoped>
.main-container {
  padding: 0;
}
.header-title {
  margin-top: 0;
  margin-bottom: 20px;
  padding: 0;
}
.title-bar {
  padding: 0;
  padding-left: 12px;
  margin-right: 10px;
}
.data-table-container {
  margin-top: 20px;
  padding-bottom: 10px;
}
.data-table-header {
  padding: 20px 10px;
}
.dates-menu {
  margin-left: 16px;
}
.menu-toolbar-title {
  border-top: 0;
  border-right: 0;
  border-left: 0;
  padding-left: 0;
  padding-right: 0;
  &--icon {
    margin-left: 6px;
  }
}
.menu-date-caption {
  font-size: 14px;
  &--subtext {
    margin-left: 12px;
    font-size: 12px;
    color: grey;
  }
}
.checkbox-container {
  margin-left: 30px;
}
.input-checkbox {
  transform: scale(1.3);
}
.compare-labels-container {
  margin-left: 10px;
}
.compare-label-title {
  color: var(--v-background-lighten1);
  font-size: 13px;
  font-weight: bold;
  &--inactive {
    font-weight: light;
    font-size: 13px;
  }
}
.reporting-table {
  border-bottom: 1px solid var(--v-background-lighten4);
}
.reporting-table /deep/ .v-table__overflow {
  position: sticky;
  border-top: 1px solid var(--v-background-lighten4);
  tbody {
    td {
      padding: 6px 14px;
      height: auto;
      vertical-align: middle;
      min-width: 80px;
    }
    td:first-child {
      vertical-align: middle;
    }
  }
  tbody tr[active] td.pinned {
    background-color: #eff4fa;
  }
  thead th:first-child,
  tbody td:first-child {
    position: sticky;
    left: 0;
    background-color: #fff;
    z-index: 2;
  }
  thead th:first-child {
    z-index: 5;
    top: 0;
    background-color: #fff;
    min-width: 250px;
  }
  tbody tr:hover {
    box-shadow: none;
  }
}
</style>
